import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Tooltip, Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CaptureResponse } from "../../types/model/capture";
import { FeatureCollection } from "../../types/model/captureLocation";
import { Entity } from "../../types/model/entity/Entity";
import { ManagementUnit } from "../../types/model/managementUnit";
import CaptureLocationMap from "./CaptureLocationMap";
import CapturesTable from "./CapturesTable";

interface FilterOptions {
  entityId?: number;
  managementUnitId?: number;
  militaryMap?: string;
  search?: string;
}

interface CaptureViewProps {
  tableData: CaptureResponse[];
  onRequestRefresh: () => void;
  onApplyFilters?: (filter: FilterOptions) => void;
  entityId?: number;
  managementUnitId?: number;
  entities?: Entity[];
  allManagementUnits?: ManagementUnit[];
  geojson?: FeatureCollection;
  mapHasData: boolean;
  page: number;
  rowsPerPage: number;
  totalCapturesCount: number;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isLoading: boolean;
}

const CaptureView = ({
  tableData,
  onRequestRefresh,
  onApplyFilters,
  entityId,
  managementUnitId,
  entities,
  allManagementUnits,
  geojson,
  mapHasData,
  page,
  rowsPerPage,
  totalCapturesCount,
  onPageChange,
  onRowsPerPageChange,
  isLoading,
}: CaptureViewProps) => {
 const { t } = useTranslation();
  const [selectedCaptures, setSelectedCaptures] = useState<number[]>([]);
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  
  const toggleOpenMap = () => {
    setIsMapOpen((prev) => !prev);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <CapturesTable
        tableData={tableData}
        entities={entities}
        allManagementUnits={allManagementUnits}
        onRequestRefresh={onRequestRefresh}
        onApplyFilters={onApplyFilters}
        isMapOpen={isMapOpen}
        toggleOpenMap={toggleOpenMap}
        entityId={entityId}
        managementUnitId={managementUnitId}
        selectedCaptures={selectedCaptures}
        setSelectedCaptures={setSelectedCaptures}
        mapHasData={mapHasData}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCapturesCount={totalCapturesCount}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        isLoading={isLoading}
      />

      <Dialog
        open={isMapOpen}
        onClose={() => setIsMapOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={isFullScreen}
        PaperProps={{
          sx: {
            height: isFullScreen ? "100%" : "lg",
          },
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontWeight: 600 }}>{t("captures.map.title")}</Typography>

          <Box sx={{ flexGrow: 1 }} />

          <Tooltip title={isFullScreen ? t("generic.exit-fullscreen") : t("generic.fullscreen")}>
            <IconButton onClick={() => setIsFullScreen((p) => !p)}>
              {isFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Tooltip>

          <Tooltip title={t("generic.close")}>
            <IconButton onClick={() => setIsMapOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </DialogTitle>
        <DialogContent dividers>
          <CaptureLocationMap
            geojson={geojson}
            selectedCaptures={selectedCaptures}
            isMaximized={isFullScreen}
            entityId={entityId}
            managementUnitId={managementUnitId}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CaptureView;